/**
 * The session guard for the signed-in screens (/admin and its pages).
 *
 * Asks the Worker once, on mount, who is signed in, and renders one of four
 * things. The four are distinct on purpose (lib/session.ts says why):
 *
 *   loading      the splash (LoadingSplash.tsx), no chrome. The dashboard's
 *                rail is not drawn around a screen that may be about to leave.
 *   signed out   nothing: the visitor is sent to /login carrying the screen
 *                they asked for, and comes straight back to it afterwards.
 *   unconfigured the deployment cannot check a session at all (no session
 *                pepper). Said plainly, with no sign-in loop to fall into.
 *   down         P3 could not be reached. One button, try again.
 *
 * Only the signed-in case renders the children, which receive the session.
 *
 * /demo never passes through here. The sample dashboard is the signed-out
 * shop window and must keep rendering for anyone with the link.
 */

import { useEffect, useState, type ReactNode } from "react";

import { LoadingSplash } from "~/app/components/LoadingSplash";
import { Button, StateCard } from "~/app/components/primitives";
import { clearLiveCache, readSession, rememberSession } from "~/app/lib/live-cache";
import { loginPathFor } from "~/app/lib/login";
import { fetchSession, type SessionPayload, type SessionState } from "~/app/lib/session";
import { navigate, useHref } from "~/app/router";

export function RequireSession({
  children,
}: {
  children: (session: SessionPayload) => ReactNode;
}) {
  /* The session this tab already checked, rendered at once so a move between
     Settings, Billing and Team does not flash the splash (Thomas, Sep 11
     2026). The check below runs once per DOCUMENT, not once per screen: this
     component stays mounted across those moves while the screens remount
     (measured in the Sep 11 rehearsal). A session ended elsewhere is still
     caught on the next move, because the five tenant reads answer 401 and
     LiveTenant clears the cache and sends the reader to /login. */
  const [state, setState] = useState<SessionState>(() => {
    const known = readSession();
    return known ? { kind: "signed-in", session: known } : { kind: "loading" };
  });
  const [attempt, setAttempt] = useState(0);
  const href = useHref();

  useEffect(() => {
    let cancelled = false;
    const known = readSession();
    if (!known) setState({ kind: "loading" });
    void fetchSession().then((next) => {
      if (cancelled) return;
      if (next.kind === "signed-in") rememberSession(next.session);
      if (next.kind === "signed-out") clearLiveCache();
      // A screen already showing its figures does not go blank because one
      // revalidation could not reach P3. The next screen puts it right.
      if (next.kind === "down" && known) return;
      setState(next);
    });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  useEffect(() => {
    if (state.kind !== "signed-out") return;
    navigate(loginPathFor(href), { replace: true });
  }, [state.kind, href]);

  switch (state.kind) {
    case "signed-in":
      return <>{children(state.session)}</>;
    case "loading":
    case "signed-out":
      return <LoadingSplash label="Checking your session" />;
    case "unconfigured":
      return (
        <div data-surface="enterprise" className="p3e-bare">
          <StateCard variant="down" title="Sign-in is not available on this deployment">
            {state.message}
          </StateCard>
          <Button variant="ghost" size="sm" onClick={() => navigate("/overview")}>
            Back to the overview
          </Button>
        </div>
      );
    case "down":
      return (
        <div data-surface="enterprise" className="p3e-bare">
          <StateCard variant="down" title="We could not reach P3">
            {state.message}
          </StateCard>
          <Button variant="secondary" size="sm" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </Button>
        </div>
      );
  }
}
